
import { useState } from 'react';

interface FAQ {
  question: string;
  answer: string;
}

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQ[] = [
    {
      question: 'When can I enroll in health insurance?',
      answer: 'For individual and family plans, the main enrollment period is during Open Enrollment (November 1 - January 15). However, you may qualify for a Special Enrollment Period if you experience certain life events like losing other coverage, getting married, having a baby, or moving. Group insurance through employers typically has specific enrollment periods set by the employer. Medicare has specific enrollment periods based on your age and circumstances. Contact us to determine your eligibility and enrollment options.'
    },
    {
      question: 'What types of health insurance plans do you offer?',
      answer: 'We offer a comprehensive range of health insurance solutions including Individual & Family Health Plans, Group/Employer Health Insurance, Medicare Supplement Plans (Medigap), Medicare Advantage Plans, Short-Term Health Insurance, and Dental & Vision Plans. As an independent agency, we work with over 20 major insurance carriers, allowing us to compare plans and find the best coverage for your specific needs and budget.'
    },
    {
      question: 'How much does health insurance cost?',
      answer: 'Health insurance costs vary widely based on several factors including the type of plan, coverage level, deductible amount, your age, location, family size, and tobacco use. Individual plans in Dallas typically range from $300-$800 per month, while family plans range from $800-$2,000+ per month. Many people qualify for subsidies that significantly reduce monthly premiums. We provide free quotes and can help you understand all available options and potential savings.'
    },
    {
      question: 'What is the advantage of using an independent insurance agent?',
      answer: 'Independent agents like Dallas Health Insurance Experts work for YOU, not the insurance companies. We have access to plans from 20+ carriers, allowing us to compare coverage and prices to find your best option. We provide unbiased advice, handle all the paperwork, assist with claims, and offer ongoing support at no additional cost to you. Insurance companies pay us directly, so our services are completely free to you while giving you access to more choices and expert guidance.'
    },
    {
      question: 'Do you charge fees for your services?',
      answer: 'No, our services are completely free to you. We are compensated directly by the insurance carriers when you enroll in a plan. You pay the same premium whether you work with us or go directly to the insurance company, but with us you get expert guidance, plan comparison, enrollment assistance, and ongoing support at no extra cost. There are no hidden fees or charges for our consultation, quotes, or services.'
    },
    {
      question: 'Can you help me if I already have health insurance?',
      answer: 'Absolutely! We can review your current coverage to ensure it still meets your needs and budget. Many people are overpaying for their current plans or have coverage gaps they don\'t realize. We can compare your existing plan with other available options and help you switch during the next enrollment period if we find better coverage or savings. We also assist with claims issues, coverage questions, and plan changes throughout the year.'
    },
    {
      question: 'What information do I need to get a health insurance quote?',
      answer: 'To provide an accurate quote, we need basic information including your age, zip code, household size, tobacco use status, and approximate household income (to determine subsidy eligibility). For group insurance, we need employee count and basic demographic information. We can provide preliminary quotes with just this basic information, and then refine options based on your specific needs, preferred doctors, medications, and budget during a consultation.'
    },
    {
      question: 'How long does it take to get enrolled in a health insurance plan?',
      answer: 'The enrollment process typically takes 1-3 business days once you\'ve selected a plan. We handle all the paperwork and submission. Your coverage effective date depends on when you enroll and the type of plan. For individual plans during Open Enrollment, coverage typically starts January 1st. For Special Enrollment Periods, coverage usually begins the first of the month following enrollment. Group insurance effective dates are set by the employer. We work efficiently to ensure you have coverage as quickly as possible.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-darkGray">
            Get answers to common questions about health insurance enrollment, costs, and our services
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-lightBlue/5 to-white rounded-xl border border-lightBlue/20 overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 flex items-center justify-between gap-4 text-left cursor-pointer hover:bg-lightBlue/5 transition-colors"
              >
                <h3 className="font-semibold text-lg text-primary pr-4">
                  {faq.question}
                </h3>
                <div className={`w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${
                  openIndex === index ? 'rotate-180' : ''
                }`}>
                  <i className="ri-arrow-down-s-line text-xl text-secondary"></i>
                </div>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-8 pb-6 text-darkGray leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center bg-gradient-to-br from-secondary/5 to-lightBlue/5 rounded-2xl p-8 border border-secondary/20">
          <h3 className="font-heading text-2xl font-bold text-primary mb-3">
            Still Have Questions?
          </h3>
          <p className="text-darkGray mb-6">
            Our insurance experts are here to help. Contact us for personalized answers to your specific situation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+12143060121"
              className="px-8 py-4 bg-secondary text-white rounded-lg font-semibold hover:bg-secondary/90 transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3"
            >
              <i className="ri-phone-line text-xl"></i>
              <span>(214) 306-0121</span>
            </a>
            <a
              href="mailto:info@dallashealthinsurance.com"
              className="px-8 py-4 bg-white text-primary border-2 border-primary rounded-lg font-semibold hover:bg-primary hover:text-white transition-all whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3"
            >
              <i className="ri-mail-line text-xl"></i>
              <span>Email Us</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
